import ArticleLayout from '../../components/ArticleLayout';

export default function RadiatorTryk() {
  return (
    <ArticleLayout
      title="Trykket i varmeanlægget er faldet: Hvad manometeret skal vise"
      metaTitle="Radiator tryk: Hvad skal manometeret vise i bar?"
      metaDescription="Er anlægstrykket faldet på dit varmeanlæg? Lær hvad manometeret skal stå på (typisk 1,5 bar), hvornår du efterfylder, og hvornår en VVS'er skal tilkaldes."
      canonicalPath="/radiator-tryk"
      introLead="Vandtrykket på varmeanlæggets manometer sikrer, at det varme radiatorvand har tilstrækkeligt tryk til at nå helt op i boligens øverste etager."
      introAnswer="I et almindeligt parcelhus skal manometeret som tommelfingerregel vise et driftstryk på ca. 1,5 bar ved koldt anlæg. Er trykket for lavt, kan det varme vand ikke nå ud til de yderste radiatorer, og anlægget skal efterfyldes."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Kontrol af anlægstryk' },
        { id: 'korrekt-tryk', title: '2. Hvad skal trykket være i dit hus?' },
        { id: 'hvorfor-falder-tryk', title: '3. Hvorfor taber varmeanlægget tryk?' },
        { id: 'ekspansion', title: '4. Den røde trykekspansionsbeholder' },
        { id: 'faq', title: '5. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: Sådan kontrollerer du varmeanlæggets tryk"
      checklist={[
        {
          title: 'Find manometeret ved varmeenheden i bryggers eller kælder',
          description: 'Manometeret er en rund trykmåler med en viser og en skala, der typisk går fra 0 til 3 eller 4 bar.',
        },
        {
          title: 'Aflæs nålens position i forhold til det grønne felt',
          description: 'I et standard enfamiliehus skal nålen stå stabilt omkring 1,5 bar (eller inden for det markerede driftsfelt).',
        },
        {
          title: 'Undersøg om der drypper vand fra sikkerhedsventilen',
          description: 'Sikkerhedsventilen (overtryksventilen ved fyret) må ikke dryppe eller løbe med vand under normal drift.',
        },
        {
          title: 'Tjek om radiatorer på 1. sal er kolde i toppen',
          description: 'Hvis trykket er for lavt, kan vandet ikke cirkulere op til boligens øverste eller yderste radiatorer, som forbliver kolde.',
        },
        {
          title: 'Kontakt en autoriseret VVS-installatør ved gentagne trykfald',
          description: 'Skal du efterfylde vand oftere end 1–2 gange årligt, har anlægget en defekt ekspansionsbeholder eller en lækage.',
        },
      ]}
      sections={[
        {
          id: 'korrekt-tryk',
          title: 'Hvor mange bar skal manometeret vise?',
          content: (
            <>
              <p>
                Ifølge vejledninger fra både Videncentret Bolius og kedelproducenter som Vaillant skal manometeret på varmeanlægget i et almindeligt parcelhus som udgangspunkt vise et driftstryk på <span className="font-mono">1,5 bar</span> ved koldt anlæg.
              </p>
              <p>
                Hvis trykket er for lavt, kan cirkulationspumpen ikke fordele det varme vand ordentligt ud i systemet, og varmen udebliver fra de yderste radiatorer. Ved påfyldning af vand på anlægget gælder følgende praktiske regler fra kilderne:
              </p>
              <ul className="list-disc pl-5 space-y-2 my-4 text-[#221E1B]">
                <li>
                  <strong>Koldt anlæg:</strong> Kedlen må kun efterfyldes i kold tilstand, og hovedafbryderen skal slukkes forud for påfyldningen.
                </li>
                <li>
                  <strong>Tøm slangen for luft:</strong> Forbind påfyldningsstudsen til en koldtvandshane med en slange, der på forhånd er fyldt med vand, så du undgår at presse luft ind i centralvarmesystemet.
                </li>
                <li>
                  <strong>Stop ved 1,5 bar:</strong> Åbn hanen langsomt og luk igen, så snart trykket når 1,5 bar. Fyldes der for meget vand på, vil sikkerhedsventilen åbne og lede overskydende vand ud.
                </li>
                <li>
                  <strong>Udluft og eftertjek:</strong> Efter påfyldning udluftes radiatorerne, hvorefter trykket kontrolleres en ekstra gang.
                </li>
              </ul>
            </>
          ),
        },
        {
          id: 'hvorfor-falder-tryk',
          title: 'Hvorfor taber varmeanlægget tryk?',
          content: (
            <>
              <p>
                Et lukket centralvarmesystem bør holde sit tryk stabilt i månedsvis. Hvis trykket regelmæssigt dykker mod 0 eller 0,5 bar, skyldes det som regel ét af tre problemer:
              </p>
              <p>
                <strong>1. Udstødt luft efter nylig udluftning:</strong> Når du lufter radiatorer ud, fjerner du volumen fra systemet. En lille tryksænkning efter udluftning er helt naturlig og kræver blot efterfyldning.
              </p>
              <p>
                <strong>2. Defekt trykekspansionsbeholder:</strong> Dette er den hyppigste tekniske fejl i enfamiliehuse.
              </p>
              <p>
                <strong>3. Skjult lækage i rør eller gulvvarme:</strong> En utæt samling under et betongulv eller en gennemtæret rørføring i krybekælderen kan lække små mængder vand, som fordamper uden at efterlade synlige pytter.
              </p>
            </>
          ),
        },
        {
          id: 'ekspansion',
          title: 'Den røde trykekspansionsbeholder: Sådan virker den',
          content: (
            <>
              <p>
                Vand udvider sig, når det opvarmes fra 20 °C til 70 °C. Da vand ikke kan komprimeres, skal denne ekstra volumen have et sted at gå hen.
              </p>
              <p>
                I anlægget sidder derfor en trykekspansionsbeholder (oftest en rød eller grå beholder på 12–25 liter). Indeni er der en gummimembran med kvælstof på den ene side og radiatorvand på den anden.
              </p>
              <p>
                Når gummimembranen efter en årrække slides eller mister sit fortryk, forsvinder den fjedrende luftpude. Så snart fyret varmer vandet op og vandet udvider sig, stiger trykket ukontrolleret, hvorefter sikkerhedsventilen åbner og leder vandet ud i afløbet. Når anlægget køler af igen, falder trykket helt i bund.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Sikkerhedsregel: Kedler, anlægstryk og VVS-arbejde',
        content: (
          <p>
            Al reparation, servicering eller udskiftning af gasfyr, oliefyr, varmevekslere, ekspansionsbeholdere og sikkerhedsventiler skal udføres af en autoriseret VVS-installatør. Forsøg aldrig at åbne kedelkapper eller justere på lukkede sikkerhedsventiler. Hvis du er i tvivl om, hvordan påfyldningshanen betjenes sikkert uden tilbageløb til drikkevandet, bør du få en fagmand til at instruere dig.
          </p>
        ),
      }}
      faq={[
        {
          q: 'Hvad sker der, hvis trykket i anlægget bliver for højt?',
          a: 'Lukkede varmeanlæg er forsynet med en lovpligtig sikkerhedsventil (overtryksventil). Hvis trykket overstiger den tilladte grænse, åbner ventilen automatisk og leder overskydende vand ud, så rør og kedel beskyttes mod overtryk.',
        },
        {
          q: 'Hvor ofte er det normalt at fylde vand på anlægget?',
          a: 'I et sundt anlæg er det tilstrækkeligt at efterfylde 1 gang om året (ved starten af fyringssæsonen). Kræver anlægget påfyldning hver eller hver anden måned, er der en utæthed eller defekt ekspansionsbeholder.',
        },
        {
          q: 'Gælder reglen om anlægstryk også for direkte fjernvarme?',
          a: 'Nej. Ved direkte fjernvarme leveres vandtrykket direkte fra byens forsyningsnetværk (typisk 4–6 bar), og du har ikke et internt manometer til påfyldning i boligen. Her er trykket fjernvarmeværkets ansvar.',
        },
      ]}
      internalLinks={[
        { to: '/udluftning-af-radiator', text: 'Udluftning af radiator', desc: 'husk at tjekke trykket efter udluftning' },
        { to: '/radiator-varmer-ikke-efter-udluftning', text: 'Radiator varmer ikke efter udluftning', desc: 'når manglende tryk stopper varmen' },
        { to: '/radiator-laekker', text: 'Radiatoren lækker', desc: 'utætheder der giver tryktab' },
        { to: '/fjernvarme-radiator-virker-ikke', text: 'Fjernvarme radiator virker ikke', desc: 'forskellen på direkte og indirekte anlæg' },
      ]}
      sources={[
        { url: 'https://www.bolius.dk/saadan-indstiller-du-varmeanlaegget-til-vinteren-6895', label: 'Indstil varmeanlægget rigtigt inden vinteren' },
        { url: 'https://www.vaillant.dk/viden/energiguide/vandpåfyldning-af-centralvarmeanlæg/', label: 'Sådan fylder du vand på dit varmeanlæg | Vaillant' },
        { url: 'https://www.vaillant.dk/viden/energiguide/tjek-dit-fyr-inden-vintersæsonen/', label: 'Tjek dit fyr inden vintersæsonen med tips fra Vaillant' },
        { url: 'https://sparenergi.dk/privat/spar-energi-i-hverdagen/gode-raad-om-drift-af-dit-gasfyr', label: 'Gode råd om drift af dit gasfyr | SparEnergi' },
      ]}
    />
  );
}
